import { Component, computed, input, Input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DataSources } from '../data-sources';
import { AnalystGauge } from '../analyst-gauge/analyst-gauge';
import { PriceLineChart } from '../price-target-chart/price-line-chart';
import { CountUp } from '../count-up/count-up';
import { V3AnalystsRatingItem } from '../v3-analysts-rating-item/v3-analysts-rating-item';
import { V3NotchCard } from '../v3-notch-card/v3-notch-card';
import { analystsAnalysisData } from '../services/analysts-analysis-data';
import { AnalystRating } from '../analysts-data';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';

// Detail-page ratings-list max-height (only relevant when isDetail — see ratingsMaxHeight).
const RATINGS_MAX_HEIGHT = 320;

// Zero/finnetWeb desktop analysts content, shared by V3AnalystsOverview (main-page card) and
// V3AnalystsAnalysis (detail sub-page) — the two differ only in spacing/chart size and whether
// the ratings list shows just the latest entry or all of them, all driven by `isDetail`. See
// CLAUDE.md.
@Component({
  selector: 'app-v3-analysts-desktop-content',
  imports: [
    AnalystGauge,
    PriceLineChart,
    V3AnalystsRatingItem,
    CountUp,
    NgTemplateOutlet,
    V3NotchCard,
    V3TabPanel,
  ],
  standalone: true,
  templateUrl: './v3-analysts-desktop-content.html',
  styleUrl: './v3-analysts-desktop-content.scss',
})
export class V3AnalystsDesktopContent {
  // input(), not @Input() — see V3AnalystsOverview's identical comment: plain @Input()
  // properties aren't tracked as computed() dependencies, so `potential` would only ever pick
  // up whichever price existed the first time it happened to run.
  readonly currentPrice = input<number | null>(null);

  // Static per-instance config, set once by the parent (overview vs. detail page) and never
  // changed afterwards — safe to read from computed()s despite being a plain @Input(). Every
  // detail-vs-overview difference (spacing, chart size, ratings-all-vs-latest, thesis header/
  // dividers, legend text) derives from this single flag rather than each being its own input.
  @Input() isDetail = false;

  private readonly data = analystsAnalysisData(this.currentPrice);

  readonly dataSources = DataSources;
  readonly isFinnet = this.data.isFinnet;

  readonly gap = computed(() => (this.isDetail ? 32 : 20));
  readonly chartHeight = computed(() => (this.isDetail ? 300 : 220));
  readonly showAllRatings = computed(() => this.isDetail);
  readonly ratingsMaxHeight = RATINGS_MAX_HEIGHT;

  readonly hasContent = this.data.hasContent;
  readonly rating = this.data.rating;
  readonly isPositive = this.data.isPositive;
  readonly buyCount = this.data.buyCount;
  readonly holdCount = this.data.holdCount;
  readonly sellCount = this.data.sellCount;
  readonly gaugeValue = this.data.gaugeValue;
  readonly sellGaugeValue = this.data.sellGaugeValue;
  readonly avgTarget = this.data.avgTarget;
  readonly highTarget = this.data.highTarget;
  readonly lowTarget = this.data.lowTarget;
  readonly targetCurrency = this.data.targetCurrency;
  readonly avgTargetEuro = this.data.avgTargetEuro;
  readonly potential = this.data.potential;
  readonly targetIsPositive = this.data.targetIsPositive;
  readonly analystCount = this.data.analystCount;
  readonly consensusHistory = this.data.consensusHistory;
  readonly isZeroChartData = this.data.isZeroChartData;
  readonly chartPrimaryColor = this.data.chartPrimaryColor;
  readonly chartTargetLabelColor = this.data.chartTargetLabelColor;
  readonly chartTargetPositiveColor = this.data.chartTargetPositiveColor;
  readonly chartTargetNegativeColor = this.data.chartTargetNegativeColor;
  readonly ratings = this.data.ratings;
  readonly hasAnyRatings = this.data.hasAnyRatings;
  readonly availableRatingTabs = this.data.availableRatingTabs;
  readonly activeRatingTab = this.data.activeRatingTab;
  readonly selectRatingTab = this.data.selectRatingTab;
  readonly ratingTabLabel = this.data.ratingTabLabel;
  readonly ratingTabCount = this.data.ratingTabCount;
  readonly ratingKey = this.data.ratingKey;
  readonly showPositiveTheses = this.data.showPositiveTheses;
  // The detail page's Hold tab shows no theses; the tabless overview keeps them.
  readonly theses = computed(() =>
    this.isDetail && this.activeRatingTab() === 'hold' ? [] : this.data.theses(),
  );

  // "37 Häuser" everywhere on the overview card; the detail page appends the source
  // ("37 Häuser · TIPRANKS LTD.") since it's not shown anywhere else on that page.
  readonly analystCountLegend = computed(() => {
    const count = this.analystCount();
    if (count === null) return '';
    const base = `${count} Häuser`;
    return this.isDetail ? `${base} · ${this.dataSources.TIPRANKS}` : base;
  });

  // What the template actually iterates — either just the latest rating ("Letztes Rating") or
  // every rating in the active tab's category ("Ratings und Thesen").
  readonly visibleRatings = computed<AnalystRating[]>(() => {
    if (!this.showAllRatings()) return this.ratings().slice(0, 1);
    const active = this.activeRatingTab();
    return active ? this.data.ratingsByCategory()[active] : [];
  });

  readonly ratingsTitle = computed(() =>
    this.showAllRatings() ? 'Ratings und Thesen' : 'Letztes Rating',
  );

  // "N Häuser [· TIPRANKS LTD.]" for the full list (same legend as Rating Konsens/
  // Durchschnittliches Preisziel above it) vs. "UBS · 08/10/2026" for a single latest rating.
  readonly ratingsLegend = computed(() => {
    if (this.showAllRatings()) return this.analystCountLegend();
    const lr = this.ratings()[0];
    if (!lr) return '';
    return [lr.firm, lr.date].filter(Boolean).join(' · ');
  });

  // Thesis section title ("Positive/Negative Thesen der Analysten") is overview-only — the
  // detail page's ratings-list title already says "Ratings und Thesen", so a second title would
  // be redundant there.
  readonly showThesisHeader = computed(() => !this.isDetail);
}
